import { Controller } from '@hotwired/stimulus';
import csrfToken from 'stimulus/csrf_token';

// Drives Ui::DocumentUploaderComponent. Handles drag-and-drop, file
// validation, and the "selected file" preview before form submission.
//
// On a valid file pick, dispatches `document-uploader-component:file-selected`
// with `{ file }` so callers can wire async upload flows if needed; while such
// an upload runs they can show the loading state via the `loading` value.
//
// `warning` flags the selected document without blocking anything. With a
// `precheckUrl`, a picked file is POSTed there as `file`, and a `{ message }`
// answer is shown as the warning.

const DROPZONE = {
  filled: 'ui-document-uploader__dropzone--filled',
  error: 'ui-document-uploader__dropzone--error',
  dragging: 'ui-document-uploader__dropzone--dragging'
};

const HINT = {
  error: 'ui-hint--error',
  warning: 'ui-hint--warning'
};

const FILE_INFO = {
  hasFile: 'ui-document-uploader__file-info--has-file',
  selector: '.ui-document-uploader__file-info'
};

export default class extends Controller {
  static targets = ['dropzone', 'dropOverlay', 'input', 'message', 'radio', 'previewButton', 'loading', 'sectionHint'];

  static values = {
    accept: String,
    maxSize: Number,
    deleteUrl: String,
    title: { type: String, default: 'Click to upload' },
    subtitle: String,
    loading: Boolean,
    warning: String,
    precheckUrl: String
  };

  // ── Lifecycle ────────────────────────────────────────────────────────────
  connect() {
    this.dragCounter = 0;
    this.boundRadioGroupChange = this.handleRadioGroupChange.bind(this);
    document.addEventListener('change', this.boundRadioGroupChange);
    this.syncInputName();
  }

  disconnect() {
    document.removeEventListener('change', this.boundRadioGroupChange);
    if (this.precheckRequest) this.precheckRequest.abort();
  }

  // Callers flip `data-document-uploader-component-loading-value` while an
  // async upload is in flight; the server renders the same two blocks.
  loadingValueChanged(loading) {
    this.element.setAttribute('aria-busy', String(loading));
    if (this.hasLoadingTarget) this.loadingTarget.hidden = !loading;
    if (this.hasDropzoneTarget) this.dropzoneTarget.hidden = loading;
  }

  // The server renders the initial warning. An error outranks it.
  warningValueChanged(_warning, previous) {
    if (previous === undefined) return;

    this.renderWarning();
  }

  renderWarning() {
    this.sectionHintTargets.forEach((hint) => {
      const radio = document.getElementById(hint.dataset.radioId);
      hint.hidden = !(radio && radio.checked);
    });
    if (!this.hasError()) this.clearError();
  }

  // Without sections, the own document is always the selected one.
  ownWarning() {
    const ownSelected = !this.hasRadioTarget || this.radioTarget.checked;
    return this.isFilled() && ownSelected ? this.warningValue : '';
  }

  // ── Public actions (wired from data-action) ──────────────────────────────
  open(event) {
    if (this.isDisabled()) return;
    if (event && event.target.closest('.ui-button, .ui-icon-button, .ui-radio')) return;

    if (this.isSelectable() && this.isFilled()) {
      this.selectOwnRadio();
      return;
    }
    this.inputTarget.click();
  }

  replace(event) {
    event.stopPropagation();
    if (this.isDisabled()) return;
    this.inputTarget.click();
  }

  keydown(event) {
    if (event.target !== this.dropzoneTarget || this.isDisabled()) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    event.stopPropagation();
    this.open(event);
  }

  fileChanged(event) {
    const file = event.target.files && event.target.files[0];
    if (file) this.processFile(file);
  }

  dragOver(event) {
    event.preventDefault();
  }

  dragEnter(event) {
    event.preventDefault();
    if (this.isDisabled()) return;
    this.dragCounter += 1;
    this.setDragging(true);
  }

  dragLeave(event) {
    event.preventDefault();
    this.dragCounter = Math.max(0, this.dragCounter - 1);
    if (this.dragCounter === 0) this.setDragging(false);
  }

  drop(event) {
    event.preventDefault();
    this.dragCounter = 0;
    this.setDragging(false);
    if (this.isDisabled()) return;

    const file = event.dataTransfer.files && event.dataTransfer.files[0];
    if (!file) return;

    if (this.hasInputTarget && typeof DataTransfer === 'function') {
      const dt = new DataTransfer();
      dt.items.add(file);
      this.inputTarget.files = dt.files;
    }
    this.processFile(file);
  }

  // ── File processing ──────────────────────────────────────────────────────
  processFile(file) {
    this.clearError();

    if (!this.isAcceptable(file)) {
      this.restoreSelection();
      this.showError(this.invalidFileMessage());
      return;
    }

    // The old warning was about the replaced document.
    this.warningValue = '';
    this.selectedFile = file;
    this.renderSelectedFile(file);
    this.dispatch('file-selected', { detail: { file } });
    this.precheck(file);
  }

  async precheck(file) {
    if (!this.precheckUrlValue) return;

    if (this.precheckRequest) this.precheckRequest.abort();
    const request = new AbortController();
    this.precheckRequest = request;

    const answer = await this.fetchPrecheck(file, request.signal);
    if (request.signal.aborted) return;

    this.warningValue = answer.message || '';
  }

  // A failed precheck shows nothing.
  async fetchPrecheck(file, signal) {
    const body = new FormData();
    body.append('file', file);

    try {
      const response = await fetch(this.precheckUrlValue, {
        method: 'POST',
        body,
        signal,
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() }
      });
      return response.ok ? await response.json() : {};
    } catch {
      return {};
    }
  }

  // The browser has already swapped the rejected file into the input, and the error we show
  // promises the chosen document survives a failed replacement.
  restoreSelection() {
    if (!this.hasInputTarget) return;

    if (!this.selectedFile || typeof DataTransfer !== 'function') {
      this.inputTarget.value = '';
      return;
    }

    const transfer = new DataTransfer();
    transfer.items.add(this.selectedFile);
    this.inputTarget.files = transfer.files;
  }

  isAcceptable(file) {
    return this.isValidType(file) && file.size <= this.maxSizeValue;
  }

  invalidFileMessage() {
    return this.isFilled()
      ? 'To replace, please select a valid format and size document'
      : 'Please select a valid format and size document';
  }

  isValidType(file) {
    if (!this.acceptValue) return true;
    const mime = (file.type || '').toLowerCase();
    const filename = file.name.toLowerCase();

    return this.acceptValue.split(',').some((pattern) => {
      const type = pattern.trim().toLowerCase();
      if (!type) return false;
      if (type.startsWith('.')) return filename.endsWith(type);
      if (type.endsWith('/*')) return mime.startsWith(type.slice(0, -1));
      if (type.includes('/')) return mime === type;
      const ext = filename.split('.').pop() || '';
      return ext === type;
    });
  }

  // ── Rendering after a successful pick ────────────────────────────────────
  renderSelectedFile(file) {
    if (!this.hasDropzoneTarget) return;

    this.markFilled(file.name);
    this.replaceFileInfo(file.name, this.formatSize(file.size));

    if (this.hasPreviewButtonTarget) this.previewButtonTarget.remove();

    if (this.hasRadioTarget) {
      this.radioTarget.checked = true;
      this.syncInputName();
      this.renderWarning();
    }
  }

  // The dropzone's name is set here because computed from its content it would also read out the
  // Preview, Replace and Delete buttons nested inside.
  markFilled(fileName) {
    const dz = this.dropzoneTarget;
    dz.classList.add(DROPZONE.filled);
    dz.classList.remove(DROPZONE.error);
    dz.setAttribute('aria-label', `Uploaded: ${fileName}`);
  }

  replaceFileInfo(name, size) {
    const fileInfo = this.dropzoneTarget.querySelector(FILE_INFO.selector);
    if (!fileInfo) return;
    fileInfo.classList.add(FILE_INFO.hasFile);
    fileInfo.replaceChildren(
      this.buildSpan('ui-document-uploader__title', name),
      this.buildSpan('ui-document-uploader__subtitle', size)
    );
  }

  buildSpan(className, text) {
    const el = document.createElement('span');
    el.className = className;
    el.textContent = text;
    return el;
  }

  // ── Delete flow ──────────────────────────────────────────────────────────
  // Wired via `dialog-component:confirm->…#confirmDelete` on the root element,
  // bubbling up from the nested Ui::DialogComponent confirmation.
  async confirmDelete() {
    if (!this.deleteUrlValue) return;

    let succeeded = false;
    try {
      const response = await fetch(this.deleteUrlValue, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
          Accept: 'application/json'
        }
      });
      succeeded = response.ok && (await response.json()).success !== false;
    } catch {
      succeeded = false;
    }

    if (!succeeded) {
      this.showError('Could not delete the document. Please try again.');
      return;
    }

    this.renderEmpty();
    this.dispatch('deleted');
  }

  renderEmpty() {
    this.clearError();
    this.selectedFile = null;
    if (this.hasInputTarget) this.inputTarget.value = '';
    if (!this.hasDropzoneTarget) return;

    const dz = this.dropzoneTarget;
    dz.classList.remove(DROPZONE.filled);
    dz.setAttribute('aria-label', 'Upload document');

    const fileInfo = dz.querySelector(FILE_INFO.selector);
    if (!fileInfo) return;
    fileInfo.classList.remove(FILE_INFO.hasFile);

    const uploadText = document.createElement('div');
    uploadText.className = 'ui-document-uploader__upload-text';
    uploadText.append(
      this.buildSpan('ui-document-uploader__upload-link', this.titleValue),
      this.buildSpan('ui-document-uploader__upload-body', 'or drop it here')
    );

    const children = [uploadText];
    if (this.subtitleValue) {
      children.push(this.buildSpan('ui-document-uploader__subtitle', this.subtitleValue));
    }
    fileInfo.replaceChildren(...children);
  }

  // ── Radio-group syncing ──────────────────────────────────────────────────
  // In selectable mode, the file input only takes part in the form submission
  // when this uploader's radio is the selected one. Picking a sibling section
  // strips the input's `name` so the binary isn't sent; re-selecting the
  // parent restores it. Keeps the input fully interactive (replace still works).
  handleRadioGroupChange(event) {
    if (!this.hasRadioTarget) return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    if (target.name !== this.radioTarget.name) return;
    this.syncInputName();
    this.renderWarning();
  }

  syncInputName() {
    if (!this.hasInputTarget || !this.hasRadioTarget) return;

    if (this.originalInputName === undefined) {
      this.originalInputName = this.inputTarget.getAttribute('name') || '';
    }
    if (this.originalInputName === '') return;

    if (this.radioTarget.checked) {
      this.inputTarget.setAttribute('name', this.originalInputName);
    } else {
      this.inputTarget.removeAttribute('name');
    }
  }

  selectOwnRadio() {
    if (!this.hasRadioTarget || this.radioTarget.checked) return;
    this.radioTarget.checked = true;
    this.syncInputName();
    this.renderWarning();
  }

  // ── Drag visuals ─────────────────────────────────────────────────────────
  setDragging(active) {
    if (!this.hasDropzoneTarget) return;
    this.dropzoneTarget.classList.toggle(DROPZONE.dragging, active);
    if (this.hasDropOverlayTarget) this.dropOverlayTarget.hidden = !active;
  }

  // ── Error message banner ─────────────────────────────────────────────────
  showError(message) {
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.add(DROPZONE.error);
    if (!this.hasMessageTarget) return;

    this.messageTarget.removeAttribute('hidden');
    this.messageTarget.classList.remove(HINT.warning);
    this.messageTarget.classList.add(HINT.error);
    this.messageTarget.setAttribute('role', 'alert');
    this.messageTarget.setAttribute('aria-live', 'assertive');
    this.setMessageText(message);
  }

  clearError() {
    if (this.hasDropzoneTarget) this.dropzoneTarget.classList.remove(DROPZONE.error);
    if (!this.hasMessageTarget) return;

    this.messageTarget.classList.remove(HINT.error);
    const warning = this.ownWarning();
    this.messageTarget.classList.toggle(HINT.warning, warning !== '');
    this.messageTarget.setAttribute('aria-live', 'polite');
    if (warning) {
      this.messageTarget.setAttribute('role', 'status');
    } else {
      this.messageTarget.removeAttribute('role');
    }

    const text = warning || this.messageTarget.dataset.defaultHint || '';
    this.setMessageText(text);
    if (text) {
      this.messageTarget.removeAttribute('hidden');
    } else {
      this.messageTarget.setAttribute('hidden', '');
    }
  }

  hasError() {
    return this.hasMessageTarget && this.messageTarget.classList.contains(HINT.error);
  }

  // The message is a Ui::HintComponent; its text lives in the
  // `[data-hint-text]` span and the error icon/colour swap is CSS-driven
  // by the `ui-hint--error` modifier.
  setMessageText(text) {
    const textEl = this.messageTarget.querySelector('[data-hint-text]');
    if (textEl) textEl.textContent = text;
  }

  // ── State predicates / utilities ─────────────────────────────────────────
  isDisabled() {
    return this.element.getAttribute('aria-busy') === 'true' ||
      (this.hasInputTarget && this.inputTarget.disabled);
  }

  isFilled() {
    return this.hasDropzoneTarget && this.dropzoneTarget.classList.contains(DROPZONE.filled);
  }

  isSelectable() {
    return this.hasRadioTarget;
  }

  formatSize(bytes) {
    if (!bytes) return '';
    const thresh = 1024;
    if (bytes < thresh) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB', 'TB'];
    let unitIndex = -1;
    let value = bytes;
    do { value /= thresh; unitIndex += 1; } while (value >= thresh && unitIndex < units.length - 1);
    return `${value.toFixed(1)} ${units[unitIndex]}`;
  }
}
